import React, { useState, useEffect, useRef } from 'react';
import {
  Settings,
  Save,
  Loader2,
  FileText,
  Receipt,
  FileCheck,
  Eye,
  Image,
  ExternalLink,
  Building2,
  Hash,
  Type,
  Minus,
  Plus,
  RotateCcw,
  Palette,
  QrCode
} from 'lucide-react';
import {
  getDocumentSettings,
  updateDocumentSettings,
  getDocumentPreview
} from '../../api/wholesale/documentSettings';
import { getBrandingConfig } from '../../api/brandingSettings';
import { getCurrencySymbol } from '../../utils/currency';
import { Link } from 'react-router-dom';
import { toast } from 'react-toastify';
import LoadingSpinner from '../../components/common/LoadingSpinner';

/**
 * WholesaleDocumentSettings Component
 * Admin interface for customizing wholesale proforma invoices, invoices, and receipts
 * Features tabbed interface for different document types
 */
const WholesaleDocumentSettings = () => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [activeTab, setActiveTab] = useState('proforma'); // 'proforma' | 'invoice' | 'receipt'
  const [settingsId, setSettingsId] = useState(null);
  const [previewBlob, setPreviewBlob] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [brandingConfig, setBrandingConfig] = useState(null);

  // Refs for debounce and PDF.js caching
  const timeoutRef = useRef(null);
  const pdfLibRef = useRef(null);
  const [config, setConfig] = useState({
    // Company Information (shared across all documents)
    company_name: '',
    company_address: '',
    company_phone: '',
    company_email: '',
    company_website: '',
    tax_id: '',
    tax_rate: 0,
    tax_label: 'VAT',
    show_tax_breakdown: true,

    // Proforma Invoice Settings
    proforma_template_id: 1,
    proforma_delivery_format: 'auto',
    proforma_show_logo: true,
    proforma_show_company_details: true,
    proforma_show_customer_details: true,
    proforma_show_product_images: false,
    proforma_footer_text: 'This is a proforma invoice for estimation purposes only.',
    proforma_footer_notes: '',
    proforma_terms_and_conditions: 'Payment terms: Net 30 days from invoice date.',

    // Invoice Settings
    invoice_template_id: 1,
    invoice_delivery_format: 'auto',
    invoice_show_logo: true,
    invoice_show_company_details: true,
    invoice_show_customer_details: true,
    invoice_show_payment_details: true,
    invoice_show_product_images: false,
    invoice_footer_text: 'Thank you for your business!',
    invoice_footer_notes: '',
    invoice_terms_and_conditions:
      'Payment is due upon receipt. Late payments may incur additional charges.',
    invoice_show_due_date: true,
    invoice_due_days: 30,

    // Receipt Settings
    receipt_template_id: 1,
    receipt_delivery_format: 'auto',
    receipt_show_logo: true,
    receipt_show_company_details: true,
    receipt_show_customer_details: true,
    receipt_show_payment_details: true,
    receipt_show_product_images: false,
    receipt_footer_text: 'Thank you for your payment!',
    receipt_footer_notes: '',
    receipt_terms_and_conditions: '',

    // Additional Display Options (per doc type)
    proforma_show_tax_number: false,
    proforma_show_barcode: false,
    proforma_show_qr_code: false,
    invoice_show_tax_number: false,
    invoice_show_barcode: false,
    invoice_show_qr_code: false,
    receipt_show_tax_number: false,
    receipt_show_barcode: false,
    receipt_show_qr_code: false,

    // Document Colors
    bg_color: '#ffffff',
    text_color: '#111827',
    accent_color: '#D4AF37',
    heading_color: '#ffffff',
    label_color: '#8c7731',
    use_branding_colors: false,

    // Font Settings
    font_weight: 'normal',
    font_sizes: {},

    // Document Formatting
    currency_symbol: '₦',
    currency_code: 'NGN',
    date_format: 'DD/MM/YYYY',
    time_format: 'HH:mm:ss',

    // PDF Settings
    paper_size: 'A4',

    // Email Settings
    email_enabled: false,
    proforma_email_subject_template: 'Proforma Invoice #{proforma_number} - {company_name}',
    proforma_email_body_template:
      'Dear {customer_name},\n\nPlease find the proforma invoice #{proforma_number} attached for your review.\n\nTotal Amount: {total_amount}\n\nBest regards,\n{company_name}',
    invoice_email_subject_template: 'Invoice #{invoice_number} - {company_name}',
    invoice_email_body_template:
      'Dear {customer_name},\n\nPlease find your invoice #{invoice_number} attached.\n\nAmount Due: {amount_due}\nDue Date: {due_date}\n\nBest regards,\n{company_name}',
    receipt_email_subject_template: 'Payment Receipt #{receipt_number} - {company_name}',
    receipt_email_body_template:
      'Dear {customer_name},\n\nThank you for your payment. Please find your receipt #{receipt_number} attached.\n\nAmount Paid: {amount_paid}\n\nBest regards,\n{company_name}',

    // WhatsApp Message Templates
    proforma_whatsapp_template:
      '📋 *Proforma Invoice*\n\nHello {customer_name},\n\nPlease find attached your Proforma Invoice #{document_number} for Order #{order_number}.\n\n💰 Total Amount: ₦{total_amount}\n\nThank you for your business!\n\n- {company_name} Team',
    invoice_whatsapp_template:
      '📄 *Invoice*\n\nHello {customer_name},\n\nPlease find attached your Invoice #{document_number} for Order #{order_number}.\n\n💰 Total Amount: ₦{total_amount}\n\nThank you for your business!\n\n- {company_name} Team',
    receipt_whatsapp_template:
      '🧾 *Receipt*\n\nHello {customer_name},\n\nPlease find attached your Receipt #{document_number} for Order #{order_number}.\n\n💰 Amount Paid: ₦{amount_paid}\n\nThank you for your payment!\n\n- {company_name} Team',
    payslip_whatsapp_template:
      '📋 *Payslip for {pay_period}*\n\nHello {employee_name},\n\nPlease find attached your payslip for {pay_period}.\n\n💵 Net Pay: {net_pay}\n\n- {company_name} Team',

    // Payslip Settings defaults
    payslip_template_id: 1,
    payslip_delivery_format: 'auto',
    payslip_show_logo: true,
    payslip_show_company_details: true,
    payslip_show_customer_details: true,
    payslip_show_payment_details: true,
    payslip_show_product_images: false,
    payslip_footer_text: 'Thank you for your hard work!',
    payslip_footer_notes: '',
    payslip_terms_and_conditions: '',
    payslip_show_tax_number: false,
    payslip_show_barcode: false,
    payslip_show_qr_code: false,
    payslip_email_subject_template: 'Payslip for {pay_period} - {company_name}',
    payslip_email_body_template:
      'Dear {employee_name},\n\nPlease find your payslip for {pay_period} attached.\n\nNet Pay: {net_pay}\n\nBest regards,\n{company_name}'
  });

  useEffect(() => {
    fetchConfig();
    fetchBrandingConfig();
  }, []);

  const fetchBrandingConfig = async () => {
    try {
      const response = await getBrandingConfig();
      setBrandingConfig(response.data);
    } catch (error) {
      console.error('Error fetching branding config:', error);
    }
  };

  // Cleanup blob on unmount
  useEffect(() => {
    return () => {
      if (previewBlob) URL.revokeObjectURL(previewBlob);
    };
  }, [previewBlob]);

  // Auto-refresh preview on config change
  useEffect(() => {
    if (!loading) {
      if (timeoutRef.current) clearTimeout(timeoutRef.current);

      timeoutRef.current = setTimeout(() => {
        refreshPreview();
      }, 800); // 800ms debounce
    }
  }, [
    activeTab,
    config[`${activeTab}_template_id`],
    config.paper_size,
    config.company_name,
    config[`${activeTab}_show_logo`],
    config[`${activeTab}_show_company_details`],
    config[`${activeTab}_show_customer_details`],
    config[`${activeTab}_footer_text`],
    config.currency_symbol,
    config.show_tax_breakdown,
    config.tax_rate
  ]);

  const fetchConfig = async () => {
    setLoading(true);
    try {
      const response = await getDocumentSettings();
      setConfig(response.data);
      setSettingsId(response.data.id);
      // Initial preview load
      refreshPreview(response.data, 'proforma');
    } catch (error) {
      toast.error('Failed to load document settings');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const refreshPreview = async (currentConfig = config, docType = activeTab) => {
    setPreviewLoading(true);
    try {
      const startTime = Date.now();
      const blob = await getDocumentPreview(docType, currentConfig);
      console.log(`[Preview] PDF fetched in ${Date.now() - startTime}ms`);

      // Cache PDF.js library reference
      if (!pdfLibRef.current) {
        const pdfjsLib = await import('pdfjs-dist');
        // Use CDN for worker (reliable across all environments)
        pdfjsLib.GlobalWorkerOptions.workerSrc = `https://unpkg.com/pdfjs-dist@${pdfjsLib.version}/build/pdf.worker.min.mjs`;
        pdfLibRef.current = pdfjsLib;
        console.log('[Preview] PDF.js library loaded');
      }
      const pdfjsLib = pdfLibRef.current;

      const renderStart = Date.now();
      const arrayBuffer = await blob.arrayBuffer();
      const loadingTask = pdfjsLib.getDocument({
        data: arrayBuffer,
        isEvalSupported: false,
        useSystemFonts: true
      });
      const pdfDoc = await loadingTask.promise;
      const page = await pdfDoc.getPage(1);

      // Lower scale = faster rendering (1.0x for fastest preview)
      const scale = 1.0;
      const viewport = page.getViewport({ scale });

      const canvas = document.createElement('canvas');
      const context = canvas.getContext('2d');
      canvas.height = viewport.height;
      canvas.width = viewport.width;

      await page.render({
        canvasContext: context,
        viewport: viewport
      }).promise;

      // Convert to JPEG blob URL (faster than base64 and more memory efficient)
      const imageBlob = await new Promise((resolve) => canvas.toBlob(resolve, 'image/jpeg', 0.85));
      const imageUrl = URL.createObjectURL(imageBlob);
      await pdfDoc.destroy();
      console.log(`[Preview] Rendered in ${Date.now() - renderStart}ms`);

      setPreviewBlob((prev) => {
        if (prev && prev.startsWith('blob:')) URL.revokeObjectURL(prev);
        return imageUrl;
      });
    } catch (error) {
      console.error('Preview generation failed:', error);
    } finally {
      setPreviewLoading(false);
    }
  };

  const handleInputChange = (field, value) => {
    setConfig((prev) => ({
      ...prev,
      [field]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);

    try {
      await updateDocumentSettings(settingsId, config);
      toast.success('Document settings updated successfully');
    } catch (error) {
      toast.error(error.response?.data?.error || 'Failed to update settings');
      console.error(error);
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <LoadingSpinner message="Loading settings..." className="min-h-screen" />;
  }

  const tabs = [
    { id: 'proforma', label: 'Proforma Invoice', icon: FileCheck },
    { id: 'invoice', label: 'Invoice', icon: FileText },
    { id: 'receipt', label: 'Receipt', icon: Receipt },
    { id: 'payslip', label: 'Payslip', icon: FileText }
  ];

  const templateOptions = [
    { id: 1, name: 'Standard', desc: 'Classic Business' },
    { id: 2, name: 'Modern', desc: 'Clean & Bold' },
    { id: 3, name: 'Bold', desc: 'High Contrast' },
    { id: 4, name: 'Minimal', desc: 'Whitespace' },
    { id: 5, name: 'Invoice', desc: 'Professional' },
    { id: 6, name: 'Detailed', desc: 'Boxed Grid' },
    { id: 7, name: 'Elegant', desc: 'Serif Luxury' },
    { id: 8, name: 'Corporate', desc: 'Formal Blue' }
  ];

  const payslipTemplateOptions = [
    { id: 1, name: 'Zoho Modern', desc: 'Elegant Split Layout' },
    { id: 2, name: 'Payroll Hero', desc: 'Compact Industrial' },
    { id: 3, name: 'Vertex42 Clean', desc: 'Spreadsheet Invoice' },
    { id: 4, name: 'Zoho Classic', desc: 'Structured Columns' },
    { id: 5, name: 'Bee Creative', desc: 'Modern Color-Blocked' }
  ];

  return (
    <div className="min-h-screen bg-secondary-offWhite p-4 lg:p-6 pb-24">
      <div className="max-w-[1400px] mx-auto">
        {/* Header */}
        <div className="bg-white rounded-lg shadow-sm p-4 sm:p-6 mb-6">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
            <div className="flex items-center gap-3">
              <Settings className="w-8 h-8 text-accent-gold" />
              <div>
                <h1 className="text-2xl font-bold text-primary-black">
                  Wholesale Document Settings
                </h1>
                <p className="text-secondary-gray text-sm mt-1">
                  Configure proforma invoices, invoices, and receipts
                </p>
              </div>
            </div>
            {/* Save button moved to sticky footer */}
            <div className="hidden sm:block"></div>
          </div>
        </div>

        {/* Document Type Tabs */}
        <div className="bg-white rounded-lg shadow-sm mb-6 overflow-hidden">
          <div className="flex border-b border-secondary-lightGray">
            {tabs.map((tab) => {
              const Icon = tab.icon;
              return (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`flex-1 flex items-center justify-center gap-2 px-6 py-4 font-medium transition-all ${
                    activeTab === tab.id
                      ? 'bg-accent-gold text-white border-b-2 border-accent-gold'
                      : 'text-secondary-gray hover:bg-secondary-offWhite hover:text-primary-black'
                  }`}
                >
                  <Icon className="w-5 h-5" />
                  <span className="hidden sm:inline">{tab.label}</span>
                  <span className="sm:hidden">{tab.label.split(' ')[0]}</span>
                </button>
              );
            })}
          </div>
        </div>

        {/* Tab Content */}
        <div className="grid grid-cols-1 xl:grid-cols-3 gap-6">
          {/* Left Column - Settings */}
          <div className="space-y-6 xl:col-span-1">
            {/* Template Selection */}
            <div className="bg-white rounded-lg shadow-sm p-6">
              <h2 className="text-lg font-semibold text-primary-black mb-4 border-b pb-2">
                Template Design - {tabs.find((t) => t.id === activeTab)?.label}
              </h2>

              <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-6">
                {(activeTab === 'payslip' ? payslipTemplateOptions : templateOptions).map(
                  (template) => {
                    const fieldName = `${activeTab}_template_id`;
                    const isSelected = (config[fieldName] || 1) === template.id;

                    return (
                      <label
                        key={template.id}
                        className={`relative flex flex-col p-3 border-2 rounded-xl cursor-pointer transition-all hover:shadow-md ${
                          isSelected
                            ? 'border-accent-gold bg-accent-gold/5 ring-1 ring-accent-gold/20'
                            : 'border-secondary-lightGray hover:border-accent-gold/50'
                        }`}
                      >
                        <input
                          type="radio"
                          name={fieldName}
                          value={template.id}
                          checked={isSelected}
                          onChange={() => handleInputChange(fieldName, template.id)}
                          className="sr-only"
                        />
                        <div className="bg-white border border-secondary-lightGray rounded h-20 mb-3 flex items-center justify-center overflow-hidden">
                          <span className="text-2xl font-bold text-gray-200">#{template.id}</span>
                        </div>
                        <span className="font-semibold text-sm text-primary-black">
                          {template.name}
                        </span>
                        <span className="text-xs text-secondary-gray">{template.desc}</span>
                        {isSelected && (
                          <div className="absolute top-2 right-2 w-5 h-5 bg-accent-gold rounded-full flex items-center justify-center">
                            <span className="text-white text-xs">✓</span>
                          </div>
                        )}
                      </label>
                    );
                  }
                )}
              </div>

              {/* PDF Settings */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-primary-black mb-2">
                    Paper Size
                  </label>
                  <div className="flex bg-gray-100 p-1 rounded-lg">
                    {['A4', 'Letter', 'A5'].map((size) => (
                      <button
                        key={size}
                        type="button"
                        onClick={() => handleInputChange('paper_size', size)}
                        className={`flex-1 py-2 text-sm font-medium rounded-md transition-all ${
                          config.paper_size === size
                            ? 'bg-accent-gold text-white shadow-sm'
                            : 'text-secondary-gray hover:text-primary-black'
                        }`}
                      >
                        {size}
                      </button>
                    ))}
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-primary-black mb-2">
                    Default Delivery Format
                  </label>
                  <div className="flex bg-gray-100 p-1 rounded-lg">
                    {[
                      { val: 'auto', label: 'Auto', icon: Settings },
                      { val: 'pdf', label: 'PDF', icon: FileText },
                      { val: 'image', label: 'Image', icon: Image }
                    ].map((fmt) => (
                      <button
                        key={fmt.val}
                        type="button"
                        onClick={() => handleInputChange(`${activeTab}_delivery_format`, fmt.val)}
                        className={`flex-1 py-2 text-sm font-medium rounded-md transition-all flex items-center justify-center gap-1 ${
                          (config[`${activeTab}_delivery_format`] || 'auto') === fmt.val
                            ? 'bg-accent-gold text-white shadow-sm'
                            : 'text-secondary-gray hover:text-primary-black'
                        }`}
                        title={fmt.val === 'auto' ? 'Best fit for device' : fmt.label}
                      >
                        <fmt.icon className="w-3.5 h-3.5" />
                        {fmt.label}
                      </button>
                    ))}
                  </div>
                </div>
              </div>
            </div>

            {/* Display Options */}
            <div className="bg-white rounded-lg shadow-sm p-6">
              <h2 className="text-lg font-semibold text-primary-black mb-4 border-b pb-2 flex items-center gap-2">
                <Eye className="w-5 h-5 text-accent-gold" />
                Display Options
              </h2>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5 mb-6">
                {[
                  { key: `${activeTab}_show_logo`, label: 'Show Company Logo', icon: Image },
                  {
                    key: `${activeTab}_show_company_details`,
                    label: 'Show Company Details',
                    icon: Building2
                  },
                  {
                    key: `${activeTab}_show_customer_details`,
                    label: 'Show Customer Details',
                    icon: Building2
                  },
                  {
                    key: `${activeTab}_show_payment_details`,
                    label: 'Show Payment Details',
                    icon: FileText,
                    hideFor: ['proforma']
                  },
                  { key: 'show_tax_breakdown', label: 'Show Tax Breakdown', icon: Receipt },
                  { key: `${activeTab}_show_tax_number`, label: 'Show Tax Number', icon: Hash },
                  { key: `${activeTab}_show_barcode`, label: 'Show Barcode', icon: Hash },
                  { key: `${activeTab}_show_qr_code`, label: 'Show QR Code', icon: QrCode },
                  ...(activeTab === 'invoice'
                    ? [{ key: 'invoice_show_due_date', label: 'Show Due Date', icon: FileCheck }]
                    : [])
                ]
                  .filter((opt) => !opt.hideFor?.includes(activeTab))
                  .map((option) => {
                    const Icon = option.icon;
                    return (
                      <label
                        key={option.key}
                        className="flex items-center justify-between p-2.5 border border-secondary-lightGray rounded-lg hover:bg-secondary-offWhite transition-colors cursor-pointer"
                      >
                        <div className="flex items-center gap-2.5">
                          <Icon className="w-3.5 h-3.5 text-secondary-gray" />
                          <span className="text-xs font-medium text-primary-black">
                            {option.label}
                          </span>
                        </div>
                        <div className="relative">
                          <input
                            type="checkbox"
                            checked={config[option.key] || false}
                            onChange={(e) => handleInputChange(option.key, e.target.checked)}
                            className="sr-only peer"
                          />
                          <div className="w-9 h-5 bg-gray-200 rounded-full peer-checked:bg-accent-gold transition-colors" />
                          <div className="absolute left-0.5 top-0.5 w-4 h-4 bg-white rounded-full shadow-sm transition-transform peer-checked:translate-x-4" />
                        </div>
                      </label>
                    );
                  })}
              </div>

              {activeTab === 'invoice' && (
                <div>
                  <label className="block text-sm font-medium text-primary-black mb-2">
                    Due Days
                  </label>
                  <input
                    type="number"
                    value={config.invoice_due_days}
                    onChange={(e) =>
                      handleInputChange('invoice_due_days', parseInt(e.target.value))
                    }
                    className="w-full px-4 py-2 border border-secondary-lightGray rounded-lg focus:ring-2 focus:ring-accent-gold focus:border-transparent outline-none"
                    min="0"
                  />
                </div>
              )}
            </div>

            {/* Document Colors */}
            <div className="bg-white rounded-lg shadow-sm p-6">
              <h2 className="text-lg font-semibold text-primary-black mb-4 border-b pb-2 flex items-center gap-2">
                <Palette className="w-5 h-5 text-accent-gold" />
                Document Colors
              </h2>

              <label className="flex items-center justify-between p-2.5 border border-secondary-lightGray rounded-lg mb-4 cursor-pointer hover:bg-secondary-offWhite transition-colors">
                <span className="text-sm font-medium text-primary-black">Use Branding</span>
                <div className="relative">
                  <input
                    type="checkbox"
                    checked={config.use_branding_colors || false}
                    onChange={(e) => handleInputChange('use_branding_colors', e.target.checked)}
                    className="sr-only peer"
                  />
                  <div className="w-9 h-5 bg-gray-200 rounded-full peer-checked:bg-accent-gold transition-colors" />
                  <div className="absolute left-0.5 top-0.5 w-4 h-4 bg-white rounded-full shadow-sm transition-transform peer-checked:translate-x-4" />
                </div>
              </label>

              <div className="grid grid-cols-2 gap-3">
                {[
                  { key: 'bg_color', label: 'Background', default: '#ffffff' },
                  { key: 'accent_color', label: 'Accent', default: '#D4AF37' },
                  { key: 'text_color', label: 'Text', default: '#111827' },
                  { key: 'heading_color', label: 'Headings', default: '#ffffff' },
                  { key: 'label_color', label: 'Labels', default: '#8c7731' }
                ].map((color) => (
                  <div key={color.key} className="space-y-1">
                    <label className="block text-xs font-medium text-secondary-gray">
                      {color.label}
                    </label>
                    <div className="flex items-center gap-2">
                      <input
                        type="color"
                        value={config[color.key] || color.default}
                        onChange={(e) => handleInputChange(color.key, e.target.value)}
                        className="w-8 h-8 rounded border border-secondary-lightGray cursor-pointer"
                      />
                      <input
                        type="text"
                        value={config[color.key] || ''}
                        onChange={(e) => handleInputChange(color.key, e.target.value)}
                        placeholder={color.default}
                        className="flex-1 px-2 py-1.5 text-xs border border-secondary-lightGray rounded-lg focus:ring-2 focus:ring-accent-gold focus:border-transparent outline-none"
                      />
                      {config[color.key] && config[color.key] !== color.default && (
                        <button
                          type="button"
                          onClick={() => handleInputChange(color.key, color.default)}
                          className="text-secondary-gray hover:text-primary-black"
                          title="Reset"
                        >
                          <RotateCcw className="w-3.5 h-3.5" />
                        </button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
              <p className="text-xs text-secondary-gray mt-3">
                Leave color fields empty for auto-contrast defaults. Leave accent empty for template
                default.
              </p>
            </div>

            {/* Font Weight */}
            <div className="bg-white rounded-lg shadow-sm p-6">
              <h2 className="text-lg font-semibold text-primary-black mb-4 border-b pb-2 flex items-center gap-2">
                <Type className="w-5 h-5 text-accent-gold" />
                Font Weight
              </h2>
              <div className="flex bg-gray-100 p-1 rounded-lg">
                {[
                  { val: '300', label: 'Light' },
                  { val: 'normal', label: 'Normal' },
                  { val: '500', label: 'Medium' },
                  { val: '600', label: 'Semi-Bold' },
                  { val: 'bold', label: 'Bold' }
                ].map((w) => (
                  <button
                    key={w.val}
                    type="button"
                    onClick={() => handleInputChange('font_weight', w.val)}
                    className={`flex-1 py-2 text-xs font-medium rounded-md transition-all ${
                      (config.font_weight || 'normal') === w.val
                        ? 'bg-accent-gold text-white shadow-sm'
                        : 'text-secondary-gray hover:text-primary-black'
                    }`}
                    style={{ fontWeight: w.val }}
                  >
                    {w.label}
                  </button>
                ))}
              </div>
            </div>

            {/* Font Sizes */}
            <div className="bg-white rounded-lg shadow-sm p-6">
              <div className="flex items-center justify-between mb-4 border-b pb-2">
                <h2 className="text-lg font-semibold text-primary-black flex items-center gap-2">
                  <Type className="w-5 h-5 text-accent-gold" />
                  Font Sizes
                </h2>
                <button
                  type="button"
                  onClick={() => handleInputChange('font_sizes', {})}
                  className="text-xs text-secondary-gray hover:text-accent-gold flex items-center gap-1 transition-colors"
                >
                  <RotateCcw className="w-3 h-3" />
                  Reset All
                </button>
              </div>

              <div className="space-y-3">
                {[
                  { key: 'company_name', label: 'Company Name', default: 14 },
                  { key: 'company_details', label: 'Address / Details', default: 6 },
                  { key: 'receipt_info', label: 'Document Info', default: 7 },
                  { key: 'items', label: 'Item Rows', default: 8 },
                  { key: 'totals', label: 'Subtotals', default: 8 },
                  { key: 'total_highlight', label: 'Grand Total', default: 10 },
                  { key: 'payment', label: 'Payment Info', default: 8 },
                  { key: 'footer', label: 'Footer', default: 7 }
                ].map((size) => {
                  const currentVal = config.font_sizes?.[size.key] || size.default;
                  const isCustom = config.font_sizes?.[size.key] !== undefined;
                  return (
                    <div key={size.key} className="flex items-center justify-between">
                      <span className="text-xs font-medium text-primary-black w-28">
                        {size.label}
                      </span>
                      <div className="flex items-center gap-2">
                        <button
                          type="button"
                          onClick={() => {
                            const newSizes = { ...(config.font_sizes || {}) };
                            newSizes[size.key] = Math.max(4, currentVal - 1);
                            handleInputChange('font_sizes', newSizes);
                          }}
                          className="w-7 h-7 flex items-center justify-center border border-secondary-lightGray rounded-md hover:bg-secondary-offWhite transition-colors"
                        >
                          <Minus className="w-3 h-3" />
                        </button>
                        <span className="w-8 text-center text-sm font-semibold text-primary-black">
                          {currentVal}
                        </span>
                        <button
                          type="button"
                          onClick={() => {
                            const newSizes = { ...(config.font_sizes || {}) };
                            newSizes[size.key] = Math.min(30, currentVal + 1);
                            handleInputChange('font_sizes', newSizes);
                          }}
                          className="w-7 h-7 flex items-center justify-center border border-secondary-lightGray rounded-md hover:bg-secondary-offWhite transition-colors"
                        >
                          <Plus className="w-3 h-3" />
                        </button>
                        <span className="text-xs text-secondary-gray w-5">px</span>
                        {isCustom && (
                          <button
                            type="button"
                            onClick={() => {
                              const newSizes = { ...(config.font_sizes || {}) };
                              delete newSizes[size.key];
                              handleInputChange('font_sizes', newSizes);
                            }}
                            className="text-secondary-gray hover:text-accent-gold transition-colors"
                            title="Reset to default"
                          >
                            <RotateCcw className="w-3 h-3" />
                          </button>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Footer & Terms */}
            <div className="bg-white rounded-lg shadow-sm p-6">
              <h2 className="text-lg font-semibold text-primary-black mb-4 border-b pb-2">
                Footer & Terms
              </h2>

              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-primary-black mb-1">
                    Footer Text
                  </label>
                  <textarea
                    value={config[`${activeTab}_footer_text`]}
                    onChange={(e) => handleInputChange(`${activeTab}_footer_text`, e.target.value)}
                    rows={2}
                    className="w-full px-4 py-2 border border-secondary-lightGray rounded-lg focus:ring-2 focus:ring-accent-gold focus:border-transparent outline-none resize-none"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-primary-black mb-1">
                    Footer Notes
                  </label>
                  <textarea
                    value={config[`${activeTab}_footer_notes`]}
                    onChange={(e) => handleInputChange(`${activeTab}_footer_notes`, e.target.value)}
                    rows={2}
                    className="w-full px-4 py-2 border border-secondary-lightGray rounded-lg focus:ring-2 focus:ring-accent-gold focus:border-transparent outline-none resize-none"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-primary-black mb-1">
                    Terms & Conditions
                  </label>
                  <textarea
                    value={config[`${activeTab}_terms_and_conditions`]}
                    onChange={(e) =>
                      handleInputChange(`${activeTab}_terms_and_conditions`, e.target.value)
                    }
                    rows={3}
                    className="w-full px-4 py-2 border border-secondary-lightGray rounded-lg focus:ring-2 focus:ring-accent-gold focus:border-transparent outline-none resize-none"
                  />
                </div>
              </div>
            </div>
          </div>

          {/* Middle Column - Live Preview */}
          <div className="xl:col-span-1 xl:sticky xl:top-6 xl:self-start">
            <div className="bg-white rounded-lg shadow-lg overflow-hidden border border-secondary-lightGray h-full">
              <div className="bg-primary-charcoal px-4 py-3 flex items-center justify-between">
                <h3 className="font-semibold text-white flex items-center gap-2">
                  <Eye className="w-4 h-4 text-accent-gold" />
                  Live Preview
                </h3>
                {previewLoading && (
                  <span className="flex items-center gap-2 text-xs text-accent-gold">
                    <Loader2 className="w-3 h-3 animate-spin" />
                    Updating...
                  </span>
                )}
              </div>

              <div className="p-4 bg-secondary-offWhite min-h-[400px] flex items-center justify-center overflow-auto">
                {previewBlob ? (
                  <img
                    src={previewBlob}
                    alt="Document Preview"
                    className={`shadow-2xl transition-opacity duration-300 ${previewLoading ? 'opacity-70' : 'opacity-100'}`}
                    style={{
                      maxWidth: '100%',
                      maxHeight: '60vh',
                      objectFit: 'contain'
                    }}
                  />
                ) : (
                  <div className="flex flex-col items-center text-secondary-gray">
                    <Loader2 className="w-8 h-8 animate-spin mb-2" />
                    <p>Generating preview...</p>
                  </div>
                )}
              </div>

              <div className="px-4 py-2 bg-white border-t border-secondary-lightGray text-xs text-center text-secondary-gray">
                Actual output may vary slightly depending on PDF viewer.
              </div>
            </div>
          </div>

          {/* Right Column - Company Info & Settings */}
          <div className="space-y-6 xl:col-span-1">
            {/* Company Information */}
            <div className="bg-white rounded-lg shadow-sm p-6">
              <div className="flex items-center justify-between mb-4 border-b pb-2">
                <h2 className="text-lg font-semibold text-primary-black">Company Information</h2>
                <div className="flex items-center gap-2">
                  <span className="text-xs text-secondary-gray font-medium">
                    Use Branding Settings
                  </span>
                  <button
                    type="button"
                    onClick={() =>
                      handleInputChange(
                        'use_company_info',
                        config.use_company_info === false ? true : false
                      )
                    }
                    className={`relative w-9 h-5 rounded-full transition-colors ${config.use_company_info !== false ? 'bg-accent-gold' : 'bg-gray-200'}`}
                  >
                    <div
                      className={`absolute top-0.5 left-0.5 w-4 h-4 bg-white rounded-full shadow-sm transition-transform ${config.use_company_info !== false ? 'translate-x-4' : 'translate-x-0'}`}
                    />
                  </button>
                </div>
              </div>

              {config.use_company_info !== false && (
                <div className="mb-4 p-3 rounded-lg border border-accent-gold/30 bg-accent-gold/5">
                  <p className="text-xs text-accent-gold font-medium">
                    Using company info from Wholesale / General Branding Settings
                  </p>
                  <p className="text-[11px] text-secondary-gray mt-0.5">
                    Company name, logo, address, phone, and email are automatically pulled from your
                    Wholesale Branding settings. Disable to set custom values for wholesale
                    documents.
                  </p>
                </div>
              )}

              <div className="grid grid-cols-1 gap-4">
                {/* Branding Display (read-only) */}
                <div className="p-4 bg-secondary-offWhite rounded-lg border border-secondary-lightGray">
                  <div className="flex items-center gap-4 mb-3">
                    {brandingConfig?.logo_url || brandingConfig?.wholesale_logo_url ? (
                      <img
                        src={
                          brandingConfig.use_separate_wholesale_branding &&
                          brandingConfig.wholesale_logo_url
                            ? brandingConfig.wholesale_logo_url
                            : brandingConfig.logo_url
                        }
                        alt="Company Logo"
                        className="w-16 h-16 object-contain rounded-lg border border-secondary-lightGray bg-white p-1"
                      />
                    ) : (
                      <div className="w-16 h-16 bg-white rounded-lg border border-secondary-lightGray flex items-center justify-center">
                        <Building2 className="w-8 h-8 text-secondary-gray" />
                      </div>
                    )}
                    <div className="flex-1">
                      <p className="text-xs text-secondary-gray mb-1">Company Name & Logo</p>
                      <p className="text-lg font-semibold text-primary-black">
                        {brandingConfig?.use_separate_wholesale_branding &&
                        brandingConfig?.wholesale_company_name
                          ? brandingConfig.wholesale_company_name
                          : brandingConfig?.company_name || 'Not configured'}
                      </p>
                    </div>
                  </div>
                  <Link
                    to="/wholesale/settings/branding"
                    className="inline-flex items-center gap-1.5 text-sm text-accent-gold hover:text-accent-gold/80 transition-colors"
                  >
                    <ExternalLink className="w-4 h-4" />
                    Edit in Branding Settings
                  </Link>
                </div>

                <div
                  className={`space-y-4 ${config.use_company_info !== false ? 'opacity-60 pointer-events-none' : ''}`}
                >
                  <div>
                    <label className="block text-sm font-medium text-primary-black mb-1">
                      Address
                    </label>
                    <textarea
                      value={
                        config.use_company_info !== false
                          ? brandingConfig?.use_separate_wholesale_branding &&
                            brandingConfig?.wholesale_company_address
                            ? brandingConfig.wholesale_company_address
                            : brandingConfig?.company_address || config.company_address
                          : config.company_address
                      }
                      onChange={(e) => handleInputChange('company_address', e.target.value)}
                      readOnly={config.use_company_info !== false}
                      rows={2}
                      className="w-full px-4 py-2 border border-secondary-lightGray rounded-lg focus:ring-2 focus:ring-accent-gold focus:border-transparent outline-none resize-none"
                    />
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-primary-black mb-1">
                        Phone
                      </label>
                      <input
                        type="text"
                        value={
                          config.use_company_info !== false
                            ? brandingConfig?.use_separate_wholesale_branding &&
                              brandingConfig?.wholesale_company_phone
                              ? brandingConfig.wholesale_company_phone
                              : brandingConfig?.company_phone || config.company_phone
                            : config.company_phone
                        }
                        onChange={(e) => handleInputChange('company_phone', e.target.value)}
                        readOnly={config.use_company_info !== false}
                        className="w-full px-4 py-2 border border-secondary-lightGray rounded-lg focus:ring-2 focus:ring-accent-gold focus:border-transparent outline-none"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-primary-black mb-1">
                        Email
                      </label>
                      <input
                        type="email"
                        value={
                          config.use_company_info !== false
                            ? brandingConfig?.use_separate_wholesale_branding &&
                              brandingConfig?.wholesale_company_email
                              ? brandingConfig.wholesale_company_email
                              : brandingConfig?.company_email || config.company_email
                            : config.company_email
                        }
                        onChange={(e) => handleInputChange('company_email', e.target.value)}
                        readOnly={config.use_company_info !== false}
                        className="w-full px-4 py-2 border border-secondary-lightGray rounded-lg focus:ring-2 focus:ring-accent-gold focus:border-transparent outline-none"
                      />
                    </div>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-primary-black mb-1">
                    Website
                  </label>
                  <input
                    type="text"
                    value={config.company_website}
                    onChange={(e) => handleInputChange('company_website', e.target.value)}
                    className="w-full px-4 py-2 border border-secondary-lightGray rounded-lg focus:ring-2 focus:ring-accent-gold focus:border-transparent outline-none"
                  />
                </div>
              </div>
            </div>

            {/* Tax Settings */}
            <div className="bg-white rounded-lg shadow-sm p-6">
              <h2 className="text-lg font-semibold text-primary-black mb-4 border-b pb-2">
                Tax Settings
              </h2>
              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-primary-black mb-1">
                      Tax ID
                    </label>
                    <input
                      type="text"
                      value={config.tax_id}
                      onChange={(e) => handleInputChange('tax_id', e.target.value)}
                      className="w-full px-4 py-2 border border-secondary-lightGray rounded-lg focus:ring-2 focus:ring-accent-gold focus:border-transparent outline-none"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-primary-black mb-1">
                      Tax Label
                    </label>
                    <input
                      type="text"
                      value={config.tax_label}
                      onChange={(e) => handleInputChange('tax_label', e.target.value)}
                      className="w-full px-4 py-2 border border-secondary-lightGray rounded-lg focus:ring-2 focus:ring-accent-gold focus:border-transparent outline-none"
                    />
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-primary-black mb-1">
                    Tax Rate (%)
                  </label>
                  <input
                    type="number"
                    step="0.01"
                    value={config.tax_rate}
                    onChange={(e) => handleInputChange('tax_rate', parseFloat(e.target.value))}
                    className="w-full px-4 py-2 border border-secondary-lightGray rounded-lg focus:ring-2 focus:ring-accent-gold focus:border-transparent outline-none"
                  />
                </div>
                <label className="flex items-center gap-3 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={config.show_tax_breakdown}
                    onChange={(e) => handleInputChange('show_tax_breakdown', e.target.checked)}
                    className="w-4 h-4 text-accent-gold rounded focus:ring-accent-gold"
                  />
                  <span className="text-sm font-medium text-primary-black">Show Tax Breakdown</span>
                </label>
              </div>
            </div>

            {/* Currency Settings */}
            <div className="bg-white rounded-lg shadow-sm p-6">
              <h2 className="text-lg font-semibold text-primary-black mb-4 border-b pb-2">
                Currency
              </h2>
              {config.use_company_info !== false && (
                <div className="mb-4 p-3 rounded-lg border border-accent-gold/30 bg-accent-gold/5">
                  <p className="text-xs text-accent-gold font-medium">
                    Using currency from Branding Settings
                  </p>
                  <p className="text-[11px] text-secondary-gray mt-0.5">
                    Currency code and symbol are automatically pulled from your Wholesale Branding
                    settings. Turn off "Use Branding Settings" in Company Information to set custom
                    document overrides.
                  </p>
                </div>
              )}
              <div
                className={`grid grid-cols-2 gap-4 ${config.use_company_info !== false ? 'opacity-60 pointer-events-none' : ''}`}
              >
                <div>
                  <label className="block text-sm font-medium text-primary-black mb-1">
                    Symbol
                  </label>
                  <input
                    type="text"
                    value={
                      config.use_company_info !== false
                        ? getCurrencySymbol(
                            brandingConfig?.use_separate_wholesale_branding &&
                              brandingConfig?.wholesale_currency
                              ? brandingConfig.wholesale_currency
                              : brandingConfig?.currency || config.currency_code || 'NGN'
                          )
                        : config.currency_symbol
                    }
                    onChange={(e) => handleInputChange('currency_symbol', e.target.value)}
                    readOnly={config.use_company_info !== false}
                    className="w-full px-4 py-2 border border-secondary-lightGray rounded-lg focus:ring-2 focus:ring-accent-gold focus:border-transparent outline-none"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-primary-black mb-1">Code</label>
                  <input
                    type="text"
                    value={
                      config.use_company_info !== false
                        ? brandingConfig?.use_separate_wholesale_branding &&
                          brandingConfig?.wholesale_currency
                          ? brandingConfig.wholesale_currency
                          : brandingConfig?.currency || config.currency_code || 'NGN'
                        : config.currency_code
                    }
                    onChange={(e) => handleInputChange('currency_code', e.target.value)}
                    readOnly={config.use_company_info !== false}
                    className="w-full px-4 py-2 border border-secondary-lightGray rounded-lg focus:ring-2 focus:ring-accent-gold focus:border-transparent outline-none"
                    maxLength={3}
                  />
                </div>
              </div>
            </div>

            {/* Email Settings */}
            <div className="bg-white rounded-lg shadow-sm p-6">
              <div className="flex items-center justify-between mb-4 border-b pb-2">
                <h2 className="text-lg font-semibold text-primary-black">Email Settings</h2>
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={config.email_enabled}
                    onChange={(e) => handleInputChange('email_enabled', e.target.checked)}
                    className="w-4 h-4 text-accent-gold rounded focus:ring-accent-gold"
                  />
                  <span className="text-sm font-medium">Enable Email</span>
                </label>
              </div>

              {config.email_enabled && (
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-primary-black mb-1">
                      {activeTab === 'proforma' && 'Proforma '}
                      {activeTab === 'invoice' && 'Invoice '}
                      {activeTab === 'receipt' && 'Receipt '}
                      {activeTab === 'payslip' && 'Payslip '}
                      Email Subject
                    </label>
                    <input
                      type="text"
                      value={config[`${activeTab}_email_subject_template`]}
                      onChange={(e) =>
                        handleInputChange(`${activeTab}_email_subject_template`, e.target.value)
                      }
                      className="w-full px-4 py-2 border border-secondary-lightGray rounded-lg focus:ring-2 focus:ring-accent-gold outline-none"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-primary-black mb-1">
                      Email Body
                    </label>
                    <textarea
                      value={config[`${activeTab}_email_body_template`]}
                      onChange={(e) =>
                        handleInputChange(`${activeTab}_email_body_template`, e.target.value)
                      }
                      rows={4}
                      className="w-full px-4 py-2 border border-secondary-lightGray rounded-lg focus:ring-2 focus:ring-accent-gold outline-none resize-none"
                    />
                    <p className="text-xs text-secondary-gray mt-1">
                      {activeTab === 'payslip' ? (
                        <>
                          Available placeholders: {'{'}employee_name{'}'}, {'{'}company_name{'}'},{' '}
                          {'{'}pay_period{'}'}, {'{'}net_pay{'}'}
                        </>
                      ) : (
                        <>
                          Available placeholders: {'{'}customer_name{'}'}, {'{'}company_name{'}'},{' '}
                          {'{'}total_amount{'}'}
                        </>
                      )}
                    </p>
                  </div>
                </div>
              )}
            </div>

            {/* WhatsApp Message Templates */}
            <div className="bg-white rounded-lg shadow-sm p-6">
              <h2 className="text-lg font-semibold text-primary-black mb-4 border-b pb-2 flex items-center gap-2">
                <svg className="w-5 h-5 text-green-600" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347z" />
                  <path
                    fillRule="evenodd"
                    clipRule="evenodd"
                    d="M12 2C6.477 2 2 6.477 2 12c0 1.89.525 3.66 1.438 5.168L2.546 20.2a1 1 0 001.254 1.254l3.032-.892A9.955 9.955 0 0012 22c5.523 0 10-4.477 10-10S17.523 2 12 2zm0 2a8 8 0 110 16 8 8 0 010-16z"
                  />
                </svg>
                WhatsApp Message Template
              </h2>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-primary-black mb-1">
                    {activeTab === 'proforma' && 'Proforma Invoice '}
                    {activeTab === 'invoice' && 'Invoice '}
                    {activeTab === 'receipt' && 'Receipt '}
                    {activeTab === 'payslip' && 'Payslip '}
                    WhatsApp Message
                  </label>
                  <textarea
                    value={config[`${activeTab}_whatsapp_template`] || ''}
                    onChange={(e) =>
                      handleInputChange(`${activeTab}_whatsapp_template`, e.target.value)
                    }
                    rows={8}
                    placeholder="Enter your custom WhatsApp message template..."
                    className="w-full px-4 py-2 border border-secondary-lightGray rounded-lg focus:ring-2 focus:ring-accent-gold outline-none resize-none font-mono text-sm"
                  />
                  <p className="text-xs text-secondary-gray mt-2">
                    {activeTab === 'payslip' ? (
                      <>
                        Available placeholders:{' '}
                        <code className="bg-gray-100 px-1 rounded">{'{employee_name}'}</code>,{' '}
                        <code className="bg-gray-100 px-1 rounded">{'{pay_period}'}</code>,{' '}
                        <code className="bg-gray-100 px-1 rounded">{'{net_pay}'}</code>,{' '}
                        <code className="bg-gray-100 px-1 rounded">{'{company_name}'}</code>
                      </>
                    ) : (
                      <>
                        Available placeholders:{' '}
                        <code className="bg-gray-100 px-1 rounded">{'{customer_name}'}</code>,{' '}
                        <code className="bg-gray-100 px-1 rounded">{'{document_number}'}</code>,{' '}
                        <code className="bg-gray-100 px-1 rounded">{'{order_number}'}</code>,{' '}
                        <code className="bg-gray-100 px-1 rounded">{'{total_amount}'}</code>,{' '}
                        <code className="bg-gray-100 px-1 rounded">{'{amount_paid}'}</code>,{' '}
                        <code className="bg-gray-100 px-1 rounded">{'{company_name}'}</code>
                      </>
                    )}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Sticky Bottom Save Bar */}
      <div className="fixed bottom-0 left-0 right-0 bg-white border-t border-secondary-lightGray p-4 shadow-lg z-50">
        <div className="max-w-[1400px] mx-auto flex items-center justify-between sm:justify-end gap-4">
          <p className="text-secondary-gray text-sm hidden sm:block">
            Changes are not saved automatically.
          </p>
          <button
            onClick={handleSubmit}
            disabled={saving}
            className="w-full sm:w-auto flex items-center justify-center gap-2 px-8 py-3 bg-accent-gold text-white font-medium rounded-lg hover:bg-accent-gold/90 transition-colors disabled:opacity-50 disabled:cursor-not-allowed shadow-md text-base"
          >
            {saving ? (
              <>
                <Loader2 className="w-5 h-5 animate-spin" />
                <span>Saving Changes...</span>
              </>
            ) : (
              <>
                <Save className="w-5 h-5" />
                <span>Save Changes</span>
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default WholesaleDocumentSettings;
